// Модальный редактор вариантов упражнения keyword_transform для преподавателей.
// Поддерживает ручное добавление предложений и автоматический разбор текста через парсер.

import { useState, useEffect } from 'react';
import {
  Modal, Form, Button, Alert, Row, Col, Badge, Table, Spinner, Collapse, InputGroup,
} from 'react-bootstrap';
import useExerciseStore from '../../store/exerciseStore';

// ── Helpers ────────────────────────────────────────────────────────────────────

function makeEmptyItem(num) {
  return { num, sentence: '', keyword: '', answer: '', explanation: '' };
}

function variantToItems(v) {
  if (!v) return [makeEmptyItem(1)];
  const sentencesDict = v.sentences || {};
  const keys = Object.keys(sentencesDict).sort((a, b) => Number(a) - Number(b));
  if (keys.length === 0) return [makeEmptyItem(1)];
  return keys.map((k) => {
    const s = sentencesDict[k];
    const wordsDict = s.words || {};
    const answersDict = s.answers || {};
    const explanationDict = s.explanation || {};

    // Count %N placeholders in the sentence to detect all blanks,
    // even when the DB record was saved with fewer positions than blanks in the text.
    const sentenceStr = s.sentence || '';
    const placeholderCount = (sentenceStr.match(/%\d+/g) || []).length;
    const storedCount = Object.keys(wordsDict).length;
    const numBlanks = Math.max(placeholderCount, storedCount, 1);

    // Build arrays indexed 1..numBlanks; missing positions become empty strings
    const keyword = Array.from({ length: numBlanks }, (_, i) => {
      const kws = wordsDict[String(i + 1)] || [];
      return Array.isArray(kws) ? kws.join(', ') : String(kws);
    }).join(' / ');

    const answer = Array.from({ length: numBlanks }, (_, i) => answersDict[String(i + 1)] || '').join(' / ');
    const explanation = Array.from({ length: numBlanks }, (_, i) => (explanationDict[String(i + 1)] || '')).join(' / ');

    return {
      num: Number(k),
      sentence: sentenceStr,
      keyword,
      answer,
      explanation,
    };
  });
}

function itemsToPayload(variantCode, items) {
  const sentences = {};
  items.forEach((it) => {
    // Replace each _____ sequence with %1, %2, … in order
    let blankCounter = 0;
    const sentence = it.sentence.trim().replace(/_{5,}/g, () => `%${++blankCounter}`);

    // Count %N placeholders actually present in the final sentence
    const placeholderCount = (sentence.match(/%\d+/g) || []).length;
    const numBlanks = Math.max(placeholderCount, blankCounter, 1);

    // Split multi-blank fields by ' / ' separator
    const keywords = it.keyword.split(/\s*\/\s*/);
    const answers = it.answer.split(/\s*\/\s*/);
    const explanations = it.explanation.split(/\s*\/\s*/);

    const wordsDict = {};
    const answersDict = {};
    const explanationDict = {};

    for (let i = 0; i < numBlanks; i++) {
      const pos = String(i + 1);
      const kw = (keywords[i] ?? keywords[0] ?? '').trim();
      wordsDict[pos] = kw ? kw.split(/\s*,\s*/).map((k) => k.trim()).filter(Boolean) : [];
      answersDict[pos] = (answers[i] ?? '').trim();
      const expl = (explanations[i] ?? '').trim();
      if (expl) explanationDict[pos] = expl;
    }

    sentences[String(it.num)] = {
      sentence,
      words: wordsDict,
      answers: answersDict,
      ...(Object.keys(explanationDict).length > 0 ? { explanation: explanationDict } : {}),
    };
  });
  return {
    variant: variantCode.trim() || null,
    sentences,
  };
}

const NUM_CIRCLE = {
  minWidth: '1.5rem',
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '50%',
  background: '#e9ecef',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  fontWeight: 700,
  color: '#6c757d',
  flexShrink: 0,
};

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ManagerKeywordTransformVariant({
  show,
  onHide,
  onSave,
  editVariant,
  exerciseId,
  typeId,
}) {
  // ── Edit-mode state ──────────────────────────────────────────────────────────
  const [variantCode, setVariantCode] = useState('');
  const [items, setItems] = useState([makeEmptyItem(1)]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Create-mode state (parse-text form) ──────────────────────────────────────
  const {
    prompts,
    fetchPrompts,
    createPrompt,
    deletePrompt,
    parseText,
  } = useExerciseStore();

  const [promptMode, setPromptMode] = useState('saved');
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [promptOpen, setPromptOpen] = useState(false);
  const [savePromptName, setSavePromptName] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [savePromptError, setSavePromptError] = useState('');
  const [deletingPromptId, setDeletingPromptId] = useState(null);

  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [parsedBlock, setParsedBlock] = useState(null);
  const [editedAnswers, setEditedAnswers] = useState({});
  const [savingVariant, setSavingVariant] = useState(false);
  const [variantSaveError, setVariantSaveError] = useState('');

  // ── Reset parse-form ──────────────────────────────────────────────────────────
  const resetParseForm = () => {
    setRawText('');
    setParsing(false);
    setParseError('');
    setParsedBlock(null);
    setEditedAnswers({});
    setSavingVariant(false);
    setVariantSaveError('');
    setPromptOpen(false);
    setSavePromptName('');
    setSavingPrompt(false);
    setSavePromptError('');
    setDeletingPromptId(null);
  };

  // ── On modal open ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!show) return;
    setError('');

    if (editVariant) {
      setVariantCode(editVariant?.variant || '');
      setItems(variantToItems(editVariant));
    } else {
      resetParseForm();
      fetchPrompts().then((list) => {
        if (list.length > 0) {
          setSelectedPromptId(String(list[0].id));
          setPrompt(list[0].body);
          setPromptMode('saved');
        } else {
          setPromptMode('custom');
          setSelectedPromptId('');
          setPrompt('');
        }
      });
    }
  }, [show, editVariant]);

  // Sync prompt body when selected saved prompt changes
  useEffect(() => {
    if (promptMode === 'saved' && selectedPromptId) {
      const found = prompts.find((p) => String(p.id) === selectedPromptId);
      if (found) {
        setPrompt(found.body);
      } else {
        setPromptMode('custom');
        setSelectedPromptId('');
        setPrompt('');
      }
    }
  }, [selectedPromptId, promptMode, prompts]);

  // ── Edit-mode handlers ────────────────────────────────────────────────────────
  const updateItem = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addItem = () => {
    setItems((prev) => [...prev, makeEmptyItem(prev.length + 1)]);
  };

  const removeItem = (idx) => {
    setItems((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((it, i) => ({ ...it, num: i + 1 }));
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const filled = items.filter((it) => it.sentence.trim());
    if (filled.length === 0) { setError('Добавьте хотя бы одно предложение'); return; }
    if (filled.some((it) => !it.answer.trim())) { setError('Укажите правильный ответ для каждого предложения'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(itemsToPayload(variantCode, filled));
      onHide();
    } catch (err) {
      setError(err?.response?.data?.detail ?? 'Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  // ── Create-mode handlers ──────────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setRawText(ev.target.result);
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  const handleParseText = async () => {
    if (!rawText.trim()) { setParseError('Введите текст упражнения'); return; }
    setParseError('');
    setParsedBlock(null);
    setParsing(true);
    try {
      const block = await parseText(rawText, Number(typeId), exerciseId, prompt);
      setParsedBlock(block);
      const initialAnswers = {};
      (block.items ?? []).forEach((item) => { initialAnswers[item.number] = item.answer; });
      setEditedAnswers(initialAnswers);
    } catch (err) {
      setParseError(err?.response?.data?.detail ?? 'Ошибка при разборе текста');
    } finally {
      setParsing(false);
    }
  };

  const handleAnswerChange = (number, value) => {
    setEditedAnswers((prev) => {
      if (prev[number] === value) return prev;
      return { ...prev, [number]: value };
    });
  };

  const handleAddVariant = async () => {
    if (!parsedBlock) { setVariantSaveError('Сначала разберите текст'); return; }
    setVariantSaveError('');
    setSavingVariant(true);
    try {
      let payload;
      if (parsedBlock.variant_id) {
        payload = { _variant_id: parsedBlock.variant_id };
      } else {
        // Group consecutive items that share the same sentence text (multi-blank sentences)
        const groups = [];
        (parsedBlock.items ?? []).forEach((item) => {
          const last = groups[groups.length - 1];
          if (last && last.sentence === item.sentence) {
            last.subItems.push(item);
          } else {
            groups.push({ sentence: item.sentence, subItems: [item] });
          }
        });

        const sentencesDict = {};
        groups.forEach((group) => {
          const sentenceNum = group.subItems[0].number;

          // Replace each _________ with %1, %2, … in order
          let blankCounter = 0;
          const sentence = group.sentence.replace(/_{5,}/g, () => `%${++blankCounter}`);

          const wordsDict = {};
          const answersDict = {};

          group.subItems.forEach((si, idx) => {
            const pos = String(idx + 1);
            wordsDict[pos] = si.keyword ? [si.keyword] : [];
            answersDict[pos] = editedAnswers[si.number] ?? si.answer ?? '';
          });

          sentencesDict[String(sentenceNum)] = {
            sentence,
            words: wordsDict,
            answers: answersDict,
            explanation: {},
          };
        });

        payload = { variant: null, sentences: sentencesDict };
      }
      await onSave(payload);
      onHide();
    } catch (err) {
      setVariantSaveError(err?.response?.data?.detail ?? 'Ошибка при добавлении варианта');
    } finally {
      setSavingVariant(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!savePromptName.trim()) { setSavePromptError('Введите название промпта'); return; }
    if (!prompt.trim()) { setSavePromptError('Промпт не может быть пустым'); return; }
    setSavePromptError('');
    setSavingPrompt(true);
    try {
      const created = await createPrompt({ name: savePromptName.trim(), body: prompt });
      setSelectedPromptId(String(created.id));
      setPromptMode('saved');
      setSavePromptName('');
    } catch (err) {
      setSavePromptError(err?.response?.data?.detail ?? 'Ошибка при сохранении промпта');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleDeletePrompt = async (id) => {
    setDeletingPromptId(id);
    try {
      await deletePrompt(id);
      if (String(id) === selectedPromptId) {
        setPromptMode('custom');
        setSelectedPromptId('');
        setPrompt('');
      }
    } finally {
      setDeletingPromptId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
          {editVariant ? 'Редактировать вариант' : 'Новый вариант'}
        </Modal.Title>
      </Modal.Header>

      {/* ── Edit mode: row-by-row form ── */}
      {editVariant ? (
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body className="p-4">
            {error && (
              <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
                {error}
              </Alert>
            )}

            <Row className="g-3 mb-4">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Код варианта</Form.Label>
                  <Form.Control
                    value={variantCode}
                    onChange={(e) => setVariantCode(e.target.value)}
                    placeholder="A01"
                    maxLength={10}
                  />
                  <Form.Text className="text-muted">Необязательно</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div
              className="d-flex gap-2 mb-1 px-1"
              style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              <div style={{ width: '1.5rem', flexShrink: 0 }} />
              <div style={{ flex: 3 }}>Предложение</div>
              <div style={{ flex: 1 }}>Слова-подсказки</div>
              <div style={{ flex: 1 }}>Правильный ответ</div>
              <div style={{ flex: 1.5 }}>Пояснение (необяз.)</div>
              <div style={{ width: '2rem', flexShrink: 0 }} />
            </div>

            <div className="d-flex flex-column gap-2 mb-3">
              {items.map((item, idx) => (
                <div key={idx} className="d-flex align-items-start gap-2">
                  <div style={NUM_CIRCLE}>{item.num}</div>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    style={{ flex: 3 }}
                    value={item.sentence}
                    onChange={(e) => updateItem(idx, 'sentence', e.target.value)}
                    placeholder="Computers %1 part of our lives. (BECOME)"
                    size="sm"
                  />
                  <Form.Control
                    style={{ flex: 1 }}
                    value={item.keyword}
                    onChange={(e) => updateItem(idx, 'keyword', e.target.value)}
                    placeholder="BECOME"
                    size="sm"
                    title="Слова-подсказки через запятую"
                  />
                  <Form.Control
                    style={{ flex: 1 }}
                    value={item.answer}
                    onChange={(e) => updateItem(idx, 'answer', e.target.value)}
                    placeholder="have become"
                    size="sm"
                  />
                  <Form.Control
                    style={{ flex: 1.5 }}
                    value={item.explanation}
                    onChange={(e) => updateItem(idx, 'explanation', e.target.value)}
                    placeholder="Present Perfect используется..."
                    size="sm"
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    type="button"
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    style={{ borderRadius: '0.4rem', padding: '0.2rem 0.45rem', flexShrink: 0 }}
                  >
                    ✕
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="outline-primary" size="sm" type="button" onClick={addItem} style={{ borderRadius: '0.4rem' }}>
              + Добавить строку
            </Button>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={onHide}>Отмена</Button>
            <Button type="submit" variant="dark" disabled={saving} style={{ fontWeight: 500 }}>
              {saving ? <Spinner size="sm" animation="border" /> : 'Сохранить'}
            </Button>
          </Modal.Footer>
        </Form>
      ) : (
        /* ── Create mode: parse-text form ── */
        <>
          <Modal.Body className="p-4">
            <div
              className="p-4"
              style={{ background: '#f8f9fc', border: '1px solid #e9ecef', borderRadius: '0.875rem' }}
            >
              <h6 className="fw-semibold mb-3" style={{ color: '#1e2a4a' }}>
                {parsedBlock ? 'Результат разбора' : 'Добавить вариант'}
              </h6>

              {!parsedBlock && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Текст упражнения</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={rawText}
                      onChange={(e) => setRawText(e.target.value)}
                      placeholder={
                        'Computers and microchips _________ part of our everyday lives. BECOME\n' +
                        'We read magazines which _________ on computers. PRODUCE'
                      }
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem', borderRadius: '0.5rem' }}
                    />
                  </Form.Group>

                  {/* Промпт OpenAI */}
                  <div className="mb-4">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-secondary text-decoration-none"
                      onClick={() => setPromptOpen((v) => !v)}
                      aria-expanded={promptOpen}
                      style={{ fontSize: '0.875rem', fontWeight: 500 }}
                    >
                      <span style={{ marginRight: '0.35rem' }}>{promptOpen ? '▾' : '▸'}</span>
                      Промпт OpenAI
                      {promptMode === 'saved' && selectedPromptId && (
                        <Badge bg="light" text="dark" className="ms-2" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          сохранённый
                        </Badge>
                      )}
                    </Button>

                    <Collapse in={promptOpen}>
                      <div
                        className="mt-2 p-3"
                        style={{ background: '#f0f1f5', border: '1px solid #e9ecef', borderRadius: '0.75rem' }}
                      >
                        <div className="d-flex gap-3 mb-3">
                          <Form.Check
                            type="radio"
                            id="kw-prompt-mode-saved"
                            name="kwPromptMode"
                            label="Сохранённый промпт"
                            checked={promptMode === 'saved'}
                            onChange={() => {
                              setPromptMode('saved');
                              if (prompts.length > 0 && !selectedPromptId) {
                                setSelectedPromptId(String(prompts[0].id));
                                setPrompt(prompts[0].body);
                              }
                            }}
                          />
                          <Form.Check
                            type="radio"
                            id="kw-prompt-mode-custom"
                            name="kwPromptMode"
                            label="Свой промпт"
                            checked={promptMode === 'custom'}
                            onChange={() => { setPromptMode('custom'); setPrompt(''); }}
                          />
                        </div>

                        {promptMode === 'saved' && (
                          <>
                            {prompts.length === 0 ? (
                              <p className="text-muted small mb-2">
                                Нет сохранённых промптов. Переключитесь на «Свой промпт».
                              </p>
                            ) : (
                              <div className="mb-2">
                                {prompts.map((p) => (
                                  <div key={p.id} className="d-flex align-items-center gap-2 mb-1">
                                    <Form.Check
                                      type="radio"
                                      id={`kw-prompt-${p.id}`}
                                      name="kwSavedPrompt"
                                      label={p.name}
                                      value={String(p.id)}
                                      checked={selectedPromptId === String(p.id)}
                                      onChange={(e) => setSelectedPromptId(e.target.value)}
                                      className="flex-grow-1"
                                    />
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      style={{ padding: '0 6px', lineHeight: '1.4', borderRadius: '0.35rem' }}
                                      disabled={deletingPromptId === p.id}
                                      onClick={() => handleDeletePrompt(p.id)}
                                      aria-label={`Удалить промпт ${p.name}`}
                                    >
                                      {deletingPromptId === p.id ? (
                                        <Spinner size="sm" animation="border" />
                                      ) : '×'}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {selectedPromptId && (
                              <Form.Control
                                as="textarea"
                                rows={4}
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                style={{ fontFamily: 'monospace', fontSize: '0.8rem', borderRadius: '0.5rem' }}
                              />
                            )}
                            <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                              Текст можно временно изменить для этого запроса.
                            </Form.Text>
                          </>
                        )}

                        {promptMode === 'custom' && (
                          <>
                            <Form.Control
                              as="textarea"
                              rows={5}
                              value={prompt}
                              onChange={(e) => setPrompt(e.target.value)}
                              placeholder="Введите системный промпт для OpenAI…"
                              style={{ fontFamily: 'monospace', fontSize: '0.8rem', borderRadius: '0.5rem' }}
                            />
                            <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                              Системный промпт, отправляемый в OpenAI вместе с пронумерованными предложениями.
                            </Form.Text>
                            <div className="mt-2">
                              {savePromptError && (
                                <Alert variant="danger" className="py-1 px-2 mb-1" style={{ fontSize: '0.8rem' }}>
                                  {savePromptError}
                                </Alert>
                              )}
                              <InputGroup size="sm">
                                <Form.Control
                                  placeholder="Название для сохранения промпта…"
                                  value={savePromptName}
                                  onChange={(e) => setSavePromptName(e.target.value)}
                                  style={{ borderRadius: '0.4rem 0 0 0.4rem' }}
                                />
                                <Button variant="outline-primary" onClick={handleSavePrompt} disabled={savingPrompt}>
                                  {savingPrompt ? <Spinner size="sm" animation="border" /> : 'Сохранить'}
                                </Button>
                              </InputGroup>
                            </div>
                          </>
                        )}
                      </div>
                    </Collapse>
                  </div>

                  {/* Кнопки разбора */}
                  <div className="d-flex gap-2 align-items-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      as="label"
                      style={{ cursor: 'pointer', borderRadius: '0.4rem', fontWeight: 500 }}
                    >
                      Загрузить .txt
                      <input type="file" accept=".txt" style={{ display: 'none' }} onChange={handleFileUpload} />
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleParseText}
                      disabled={parsing}
                      style={{ borderRadius: '0.4rem', fontWeight: 500 }}
                    >
                      {parsing ? (
                        <><Spinner size="sm" animation="border" className="me-1" />Разбираем…</>
                      ) : 'Разобрать текст'}
                    </Button>
                  </div>
                </>
              )}

              {parseError && (
                <Alert variant="danger" className="py-2 px-3 mt-3" style={{ fontSize: '0.875rem' }}>
                  {parseError}
                </Alert>
              )}

              {parsedBlock?.ai_error && (
                <Alert variant="warning" className="py-2 px-3 mt-3" style={{ fontSize: '0.875rem' }}>
                  Ответы не сгенерированы: {parsedBlock.ai_error}
                </Alert>
              )}

              {/* Результат разбора */}
              {parsedBlock && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg="success" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {(parsedBlock.items ?? []).length} пунктов
                      </Badge>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-secondary text-decoration-none"
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => { setParsedBlock(null); setRawText(''); setEditedAnswers({}); setParseError(''); }}
                    >
                      ← Изменить текст
                    </Button>
                  </div>

                  <Table bordered hover size="sm" responsive className="mb-3">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '3rem' }}>#</th>
                        <th>Предложение</th>
                        <th style={{ width: '12rem' }}>Ключевое слово</th>
                        <th style={{ width: '15rem' }}>Правильный ответ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const groupedItems = [];
                        (parsedBlock.items ?? []).forEach((item) => {
                          const last = groupedItems[groupedItems.length - 1];
                          if (last && last.sentence === item.sentence) {
                            last.subItems.push(item);
                          } else {
                            groupedItems.push({ sentence: item.sentence, subItems: [item] });
                          }
                        });
                        return groupedItems.map((group) => (
                          <tr key={group.subItems[0].number}>
                            <td className="text-muted fw-medium">{group.subItems[0].number}</td>
                            <td style={{ fontSize: '0.8125rem', color: '#495057' }}>{group.sentence}</td>
                            <td>
                              {group.subItems.map((si, idx) => (
                                <div key={si.number} style={idx < group.subItems.length - 1 ? { marginBottom: '0.3rem' } : undefined}>
                                  <code
                                    style={{
                                      fontSize: '0.8rem',
                                      background: 'rgba(79,110,247,0.08)',
                                      padding: '0.15rem 0.4rem',
                                      borderRadius: '0.3rem',
                                      color: 'var(--bs-primary)',
                                    }}
                                  >
                                    {si.keyword}
                                  </code>
                                </div>
                              ))}
                            </td>
                            <td>
                              {group.subItems.map((si, idx) => (
                                <div key={si.number} style={idx < group.subItems.length - 1 ? { marginBottom: '0.3rem' } : undefined}>
                                  <Form.Control
                                    size="sm"
                                    value={editedAnswers[si.number] ?? ''}
                                    onChange={(e) => handleAnswerChange(si.number, e.target.value)}
                                    aria-label={`Ответ на пункт ${si.number}`}
                                    style={{ borderRadius: '0.4rem' }}
                                  />
                                </div>
                              ))}
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </Table>

                  {variantSaveError && (
                    <Alert variant="danger" className="py-2 px-3 mb-2" style={{ fontSize: '0.875rem' }}>
                      {variantSaveError}
                    </Alert>
                  )}
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>Отмена</Button>
            {parsedBlock && (
              <Button
                variant="success"
                onClick={handleAddVariant}
                disabled={savingVariant}
                style={{ fontWeight: 500 }}
              >
                {savingVariant ? (
                  <><Spinner size="sm" animation="border" className="me-1" />Сохраняем…</>
                ) : 'Сохранить вариант'}
              </Button>
            )}
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}
